import {
  Box,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import HomeIcon from '@material-ui/icons/Home'
import Dayjs from 'dayjs'
import React from 'react'

import { fetchScopedCentres } from 'api'
import {
  fetchCapexSheet,
  fetchCentreDashboard,
  fetchCycleWindow,
  fetchNotifications
} from 'api/procurement'
import { useOpsCentre } from 'app/hooks/useOpsCentre'

import AfterschoolGamesPage, { afterschoolSummary } from '../AfterschoolGames'
import CapexPage from '../Capex'
import ProcurementThemeProvider from '../components/ProcurementThemeProvider'
import { defaultOrderMonth, orderCentres } from '../constants'
import ConsumablesPage from '../Consumables'
import DashboardPage from '../Dashboard'
import CataloguePage from '../Hq/HqCatalogue'
import NotificationsPage from '../Notifications'
import PettyCashPage from '../PettyCash'
import { useRmApprovalAccess } from '../useRmApprovalAccess'
import ProgramMaterialPage from '../ProgramMaterial'

// Centre-team view, two levels ("Variant C", 2026-08-20):
//   1. Landing = a cell board (one cell per section, glanceable status).
//   2. Inside a section the same cells collapse into a slim section row on
//      top, so switching sections never means going back. ⌂ returns to the
//      board. Monthly Cadence keeps the original five category tabs below
//      the section row; each centre-aware page still receives `centreId`.
const MONTHLY_TABS = [
  { id: 'dashboard', label: 'Dashboard', Comp: DashboardPage },
  {
    id: 'program-material',
    label: 'Program Material',
    Comp: ProgramMaterialPage
  },
  { id: 'consumables', label: 'Consumables', Comp: ConsumablesPage },
  { id: 'petty-cash', label: 'Petty Cash', Comp: PettyCashPage },
  // Repair & Maintenance reuses the Petty Cash page with its own category list.
  {
    id: 'repair-maintenance',
    label: 'Repair & Maintenance',
    Comp: PettyCashPage,
    extraProps: { flow: 'repair_maintenance' }
  }
]

// R&M spent on HQ approval, outside the budget (Satya, 5 Nov 2026). Same
// ledger as R&M; shown only to Azam + super admins (useRmApprovalAccess).
const RM_APPROVAL_TAB = {
  id: 'rm-approval',
  label: 'R&M approval',
  Comp: PettyCashPage,
  extraProps: { flow: 'rm_approval' }
}

// Inline stroke icons for the landing cards ("Variant A", user 2026-08-22) —
// no icon-font dependency, tinted by the tile behind them.
const ICONS = {
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  ),
  dice: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <circle cx="9" cy="9" r="1.3" fill="currentColor" />
      <circle cx="15" cy="15" r="1.3" fill="currentColor" />
      <circle cx="15" cy="9" r="1.3" fill="currentColor" />
      <circle cx="9" cy="15" r="1.3" fill="currentColor" />
    </svg>
  ),
  bars: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 20V10M12 20V4M18 20v-7" />
    </svg>
  ),
  bell: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8a6 6 0 10-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
      <path d="M10 21a2 2 0 004 0" />
    </svg>
  ),
  gear: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19 12a7 7 0 01-.1 1.2l2 1.6-2 3.4-2.4-1a7 7 0 01-2 1.2L14 21h-4l-.5-2.6a7 7 0 01-2-1.2l-2.4 1-2-3.4 2-1.6A7 7 0 015 12a7 7 0 01.1-1.2l-2-1.6 2-3.4 2.4 1a7 7 0 012-1.2L10 3h4l.5 2.6a7 7 0 012 1.2l2.4-1 2 3.4-2 1.6c.07.4.1.8.1 1.2z" />
    </svg>
  )
}

const SECTIONS = [
  {
    id: 'monthly',
    label: 'Monthly Cadence',
    blurb: 'Budgets and spends for the month',
    icon: 'calendar',
    tint: 'blue',
    chips: MONTHLY_TABS.map((t) => t.label)
  },
  // Monthly Capex sits before Afterschool Games (user 2026-08-22) — it is the
  // recurring cycle; afterschool is a one-time drive.
  {
    id: 'capex',
    label: 'Monthly Capex',
    blurb: 'Monthly changing list — request what the centre is missing',
    icon: 'bars',
    tint: 'orange'
  },
  {
    id: 'afterschool',
    label: 'Afterschool Games',
    blurb: 'Stock check on the 57-game set',
    icon: 'dice',
    tint: 'orange'
  },
  {
    id: 'notifications',
    label: 'Notifications',
    blurb: 'Deliveries due and orders to receive',
    icon: 'bell',
    tint: 'blue',
    minor: true
  },
  {
    id: 'settings',
    label: 'Settings',
    blurb: 'Catalogue',
    icon: 'gear',
    tint: 'grey',
    minor: true
  }
]

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

const CAPEX_PILL = {
  draft: { text: 'draft', tone: 'amber' },
  submitted: { text: 'submitted', tone: 'green' },
  approved: { text: 'approved', tone: 'green' },
  rejected: { text: 'needs changes', tone: 'red' },
  sent: { text: 'ordered', tone: 'green' },
  received: { text: 'received', tone: 'green' }
}

// The month's petty-cash book closes on this day of the FOLLOWING month
// (August's entries are due by 3 September — user 2026-08-22). Drives the
// close-out phase of the banner. A WARNING only — nothing locks or blocks;
// the timelines are being learned before any restriction is added.
const PETTY_CLOSE_DAY = 3

const CENTRE_KEY = 'procurement.centerTeam.centre'
const TAB_KEY = 'procurement.centerTeam.tab'
const SECTION_KEY = 'procurement.centerTeam.section'

// Pre-restructure TAB_KEY values that are now sections of their own.
const LEGACY_TAB_SECTIONS = {
  notifications: 'notifications',
  catalogue: 'settings'
}

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  // One top row: section tabs (or nothing on the landing) left, centre picker
  // right — no dedicated header row (user 2026-08-20: "no space wastage").
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    borderBottom: '1px solid #eee',
    marginBottom: theme.spacing(1)
  },
  topSpacer: { flex: 1 },
  select: { minWidth: 230, marginBottom: 6 },
  // Landing header — the board had no title at all, which read as unfinished
  // (user clip 2026-08-22: "so much space… looks incomplete").
  landingHead: { marginBottom: theme.spacing(0.5) },
  landingTitle: { fontWeight: 700, fontSize: 22, letterSpacing: '-0.01em' },
  landingSub: { color: theme.palette.text.secondary, fontSize: 13.5 },
  // Month strip (from mockup Variant B, folded onto A per the same clip):
  // whatever is time-boxed this month, with one call-to-action.
  strip: {
    background: '#282e3c',
    color: '#f4f2ee',
    borderRadius: 14,
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3.5),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap'
  },
  stripMonth: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    color: '#f2a48f'
  },
  stripHeadline: { fontSize: 18, fontWeight: 700, letterSpacing: '-0.005em' },
  stripSub: { fontSize: 13, color: '#aeb4c2', marginTop: 2 },
  // Stats align as one row of equal blocks — label and value both single-line
  // and left-aligned, so nothing wraps to a different height (user clip
  // 2026-08-22: "not uniform… somewhere lower, somewhere left/right").
  stripStats: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(3.5),
    marginLeft: 'auto',
    paddingRight: theme.spacing(1)
  },
  stripStatLink: {
    cursor: 'pointer',
    '&:hover $stripV': { textDecoration: 'underline' }
  },
  stripK: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    color: '#8f97a8',
    whiteSpace: 'nowrap'
  },
  stripV: { fontSize: 17, fontWeight: 700, whiteSpace: 'nowrap' },
  // Landing cell board — auto-fit so the five cards fill the row edge to edge
  // on a wide screen (one row of 5) and wrap to 3+2 / 2+2+1 on smaller ones.
  cells: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(285px, 1fr))',
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(2)
  },
  // Notifications + Settings are utility sections, not work areas — a slim
  // full-width pair keeps every row of the board filled at any width.
  cellsMinor: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(285px, 1fr))',
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(2.5)
  },
  cellMinor: {
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    borderRadius: 14,
    transition: 'transform .15s ease, box-shadow .15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 22px rgba(40,46,60,.10)',
      borderColor: theme.palette.primary.main
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&:hover': { transform: 'none' }
    }
  },
  cellMinorSig: {
    color: theme.palette.text.secondary,
    fontSize: 13.5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  cellMinorRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    flex: 'none'
  },
  // The landing composes to the viewport bottom — the activity panel
  // absorbs the slack, so the page never ends mid-air (user clip round 3).
  landingWrap: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 130px)'
  },
  // Recent-activity band under the cards — real orders from the dashboard
  // API plus the latest notifications; a designed empty state when quiet.
  activity: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2, 2.5),
    flex: 1
  },
  activityTitle: {
    fontWeight: 700,
    fontSize: 15,
    marginBottom: theme.spacing(1)
  },
  activityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    borderTop: '1px solid #f1efe9',
    fontSize: 13.5
  },
  activityAmount: {
    marginLeft: 'auto',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  },
  // Variant A status cards (user pick 2026-08-22): icon tile + live signal
  // lines + a state pill, hover lift. The empty box board read as dated.
  cell: {
    padding: theme.spacing(2.25),
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    minHeight: 158,
    borderRadius: 14,
    transition: 'transform .15s ease, box-shadow .15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 22px rgba(40,46,60,.10)',
      borderColor: theme.palette.primary.main
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&:hover': { transform: 'none' }
    }
  },
  cellHead: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) },
  iconTile: {
    width: 38,
    height: 38,
    borderRadius: 10,
    flex: 'none',
    display: 'grid',
    placeItems: 'center',
    '& svg': { width: 20, height: 20 }
  },
  tintOrange: { background: '#fdeae3', color: theme.palette.primary.main },
  tintBlue: { background: '#e9eef6', color: '#41546e' },
  tintGrey: { background: '#efedea', color: theme.palette.text.secondary },
  cellTitle: { fontWeight: 700, fontSize: 17, letterSpacing: '-0.005em' },
  cellArrow: {
    marginLeft: 'auto',
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 16
  },
  sigList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    fontSize: 13.5,
    color: '#4c5361'
  },
  sig: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  sigStrong: { fontWeight: 700, color: theme.palette.text.primary },
  dot: { width: 7, height: 7, borderRadius: '50%', flex: 'none' },
  dotRed: { background: '#c6392c' },
  dotAmber: { background: '#e08a00' },
  dotGreen: { background: '#2e7d32' },
  dotGrey: { background: '#b8bcc6' },
  cellFoot: {
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap'
  },
  pill: {
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 99,
    padding: '2.5px 9px'
  },
  pillRed: { background: '#fbe9e7', color: '#b3352a' },
  pillAmber: { background: '#fdf1dc', color: '#9a6100' },
  pillGreen: { background: '#e8f2e9', color: '#2e7d32' },
  pillGrey: { background: '#efedea', color: theme.palette.text.secondary },
  cellChip: {
    fontSize: 11,
    background: '#f4f1ea',
    color: theme.palette.text.secondary,
    borderRadius: 99,
    padding: '2px 8px'
  },
  // Slim section row shown once inside a section (border lives on topRow)
  sectionTabs: { minHeight: 40 },
  sectionTab: {
    minWidth: 0,
    padding: theme.spacing(1, 2),
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0
  },
  homeTab: { minWidth: 48 },
  tabs: { borderBottom: '1px solid #eee', marginBottom: theme.spacing(1) },
  tab: {
    minWidth: 0,
    padding: theme.spacing(1.25, 2),
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: 0
  }
}))

const ProcurementCenterTeam = () => {
  const classes = useStyles()
  const [centres, setCentres] = React.useState([])
  // Shared by every Centre Operations page and kept across refreshes. This
  // page needs one centre, so a saved "All centres" opens on the first.
  const [centreId, setCentreId, pickCentre] = useOpsCentre({
    allowAll: false,
    legacyKey: CENTRE_KEY
  })
  const [section, setSection] = React.useState(() => {
    const saved = window.localStorage.getItem(SECTION_KEY)
    if (saved === 'home' || SECTIONS.some((s) => s.id === saved && !s.disabled))
      return saved
    // First visit after the restructure: honor an old notifications/catalogue
    // tab choice, else land on the board.
    return LEGACY_TAB_SECTIONS[window.localStorage.getItem(TAB_KEY)] || 'home'
  })
  const [tab, setTab] = React.useState(() => {
    const saved = window.localStorage.getItem(TAB_KEY)
    return MONTHLY_TABS.some((t) => t.id === saved) ||
      saved === RM_APPROVAL_TAB.id
      ? saved
      : 'dashboard'
  })
  const rmApprovalOk = useRmApprovalAccess()
  const monthlyTabs = React.useMemo(
    () => (rmApprovalOk ? [...MONTHLY_TABS, RM_APPROVAL_TAB] : MONTHLY_TABS),
    [rmApprovalOk]
  )
  // Set when a notification deep-links to a category+month; consumed by the
  // freshly-mounted target page (children remount on tab change). {tab, month}.
  const [navTarget, setNavTarget] = React.useState(null)
  // Live card signals for the landing board. Each loads independently and a
  // failure just leaves that card quiet — the board never blocks on them.
  const [signals, setSignals] = React.useState({})

  const centreName = centres.find((c) => c.uuid === centreId)?.name || ''

  React.useEffect(() => {
    if (!centreId) return
    let alive = true
    setSignals({ afterschool: afterschoolSummary(centreId, centreName) })
    const thisMonth = Dayjs().format('YYYY-MM-01')
    fetchCentreDashboard({ centre: centreId, month: thisMonth })
      .then((res) => {
        if (!alive) return
        const pool = res?.pool || {}
        setSignals((prev) => ({
          ...prev,
          monthly: {
            base: Number(pool.totalEffective || 0),
            used: Number(pool.totalUtilised || 0),
            over: !!pool.over
          },
          activity: res?.activity || []
        }))
      })
      .catch(() => {})
    fetchCapexSheet(centreId, defaultOrderMonth())
      .then((res) => {
        if (!alive) return
        setSignals((prev) => ({
          ...prev,
          capex: {
            open: (res?.items || []).length > 0,
            status: res?.status || 'draft',
            budget: res?.budget,
            total: Number(res?.total || 0)
          }
        }))
      })
      .catch(() => {})
    fetchCycleWindow(centreId)
      .then((res) => {
        if (!alive) return
        setSignals((prev) => ({ ...prev, window: res }))
      })
      .catch(() => {
        // LOCAL PREVIEW ONLY (vite dev server; never true in any build):
        // until the cycle-window endpoint reaches the dev API, synthesize the
        // window from Gordon's defaults (opens 18th, deadline 29th) so the
        // banner can be reviewed on localhost. PM/consumables read as due.
        if (!alive || !import.meta.env.DEV) return
        const today = Dayjs()
        const deadline = today.date(29)
        const opens = today.date(18)
        setSignals((prev) => ({
          ...prev,
          window: {
            order_month: today.add(1, 'month').format('YYYY-MM-01'),
            opens: opens.format('YYYY-MM-DD'),
            deadline: deadline.format('YYYY-MM-DD'),
            today: today.format('YYYY-MM-DD'),
            open:
              !today.isBefore(opens, 'day') && !today.isAfter(deadline, 'day'),
            pm_submitted: false,
            cons_submitted: false
          }
        }))
      })
    fetchNotifications(centreId)
      .then((res) => {
        if (!alive) return
        const items = res?.results || []
        setSignals((prev) => ({
          ...prev,
          notifications: {
            unread: items.filter((n) => !n.read).length,
            latest: items.slice(0, 4)
          }
        }))
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [centreId, centreName])

  // Jump to a category tab at a given month (YYYY-MM-01) from a notification.
  const handleNavigate = (tabId, monthIso) => {
    if (!MONTHLY_TABS.some((t) => t.id === tabId)) return
    setNavTarget({ tab: tabId, month: String(monthIso || '').slice(0, 7) })
    setSection('monthly')
    setTab(tabId)
  }

  React.useEffect(() => {
    ;(async () => {
      try {
        // Scoped to the user's own centres. NOTE: api/procurement's
        // fetchMyCentres returns EVERY centre despite the name — the
        // oh_procurement API is not centre-scoped in practice (its HQ gate is
        // the Django ADMIN group, which nearly every admin is in). This narrows
        // the PICKER only; the procurement endpoints still answer for any
        // centre asked directly. Deliberate (user, 2026-08-07).
        const res = await fetchScopedCentres()
        const list = orderCentres(res?.results || [])
        setCentres(list)
        // keep the saved centre if still valid, else default to the first
        setCentreId((prev) =>
          prev && list.some((c) => c.uuid === prev) ? prev : list[0]?.uuid || ''
        )
      } catch (e) {
        /* child pages surface their own load errors */
      }
    })()
  }, [])

  React.useEffect(() => {
    window.localStorage.setItem(TAB_KEY, tab)
  }, [tab])
  React.useEffect(() => {
    window.localStorage.setItem(SECTION_KEY, section)
  }, [section])

  // Compact, right-aligned; shares the top row with the section tabs.
  const centrePicker = (
    <TextField
      select
      className={classes.select}
      size="small"
      variant="outlined"
      label="Centre"
      value={centreId}
      onChange={(e) => pickCentre(e.target.value)}
      InputProps={{ style: { fontWeight: 600 } }}
    >
      {centres.map((c) => (
        <MenuItem key={c.uuid} value={c.uuid}>
          {c.name}
        </MenuItem>
      ))}
    </TextField>
  )

  // What each card says today, from the signals that have loaded so far.
  // Falls back to the static blurb while (or if) a signal never arrives.
  const cardContent = (s) => {
    const sigs = []
    let pill = null
    if (s.id === 'monthly' && signals.monthly) {
      const m = signals.monthly
      if (m.base > 0) {
        sigs.push({
          dot: m.over ? 'Red' : 'Green',
          text: (
            <>
              <span className={classes.sigStrong}>{inr(m.used)}</span>
              &nbsp;of {inr(m.base)} used this month
            </>
          )
        })
        if (m.over) pill = { text: 'over budget', tone: 'Red' }
      } else {
        sigs.push({ dot: 'Grey', text: 'no budgets set for this month yet' })
      }
    }
    if (s.id === 'afterschool' && signals.afterschool) {
      const a = signals.afterschool
      if (a.shortGames > 0) {
        sigs.push({
          dot: 'Red',
          text: (
            <>
              <span className={classes.sigStrong}>{a.shortGames}</span>
              &nbsp;game{a.shortGames === 1 ? '' : 's'} short ·{' '}
              <span className={classes.sigStrong}>{inr(a.total)}</span>
              &nbsp;to order
            </>
          )
        })
        sigs.push({
          dot: 'Grey',
          text: 'counts pre-filled from the July audit'
        })
        pill = { text: 'stock check open', tone: 'Red' }
      } else {
        sigs.push({ dot: 'Green', text: 'nothing short right now' })
        pill = { text: 'quiet', tone: 'Grey' }
      }
    }
    if (s.id === 'capex' && signals.capex) {
      const c = signals.capex
      const monthName = Dayjs(`${defaultOrderMonth()}-01`).format('MMMM')
      if (!c.open) {
        sigs.push({ dot: 'Grey', text: `waiting for HQ's ${monthName} list` })
        pill = { text: 'not open yet', tone: 'Grey' }
      } else {
        sigs.push({
          dot: 'Amber',
          text: (
            <>
              {monthName} open · budget&nbsp;
              <span className={classes.sigStrong}>
                {c.budget != null ? inr(c.budget) : 'not set'}
              </span>
            </>
          )
        })
        sigs.push({
          dot: 'Grey',
          text:
            c.total > 0 ? (
              <>
                requested&nbsp;
                <span className={classes.sigStrong}>{inr(c.total)}</span>
              </>
            ) : (
              'nothing requested yet'
            )
        })
        const p = CAPEX_PILL[c.status] || CAPEX_PILL.draft
        pill = { text: p.text, tone: p.tone[0].toUpperCase() + p.tone.slice(1) }
      }
    }
    if (s.id === 'notifications' && signals.notifications) {
      const n = signals.notifications
      if (n.unread > 0) {
        sigs.push({
          dot: 'Amber',
          text: (
            <>
              <span className={classes.sigStrong}>{n.unread}</span>
              &nbsp;unread notification{n.unread === 1 ? '' : 's'}
            </>
          )
        })
        pill = { text: `${n.unread} unread`, tone: 'Amber' }
      } else {
        sigs.push({ dot: 'Grey', text: 'all caught up' })
        pill = { text: 'quiet', tone: 'Grey' }
      }
    }
    if (s.id === 'settings') {
      sigs.push({ dot: 'Grey', text: 'catalogue · read-only' })
      pill = { text: 'quiet', tone: 'Grey' }
    }
    return { sigs, pill }
  }

  const tintClass = {
    orange: classes.tintOrange,
    blue: classes.tintBlue,
    grey: classes.tintGrey
  }

  // The banner runs on the monthly ORDER WINDOW (user 2026-08-22: a timeline
  // the seniors can't challenge). The calendar comes from Gordon's
  // ReminderControl (start/deadline days, HQ-tunable in Alfred) via
  // alfred/cycle-window — the same clock as the Slack reminders. The deadline
  // is a TARGET: the ask stays up through the straggle days after it, with
  // "closed" wording, until HQ's processing. Send-backs are rare and happen
  // AFTER the window, so they never drive the banner — they live on the capex
  // card ("needs changes") and in notifications.
  const stripFor = () => {
    const w = signals.window
    if (!w) return null
    const inWindow = !!w.open
    // Close-out phase: from the day after the deadline until the previous
    // month's petty cash closes (the 3rd) — covers the forms straggle
    // (centres routinely send on the 30th/1st/2nd) AND the petty-cash ask.
    const pettyClose = Dayjs(w.order_month).date(PETTY_CLOSE_DAY)
    const inCloseout =
      !inWindow &&
      Dayjs(w.today).isAfter(Dayjs(w.deadline)) &&
      !Dayjs(w.today).isAfter(pettyClose)
    if (!inWindow && !inCloseout) return null

    const m = signals.monthly
    const c = signals.capex
    const dueLabel = Dayjs(w.deadline).format('D MMMM')
    const daysLeft = Dayjs(w.deadline).diff(Dayjs(w.today), 'day')
    const daysLeftLabel = daysLeft === 0 ? 'today' : String(daysLeft)
    const orderMonthName = Dayjs(w.order_month).format('MMMM')
    const cycleMonthName = Dayjs(w.deadline).format('MMMM')
    const pettyCloseLabel = pettyClose.format('D MMMM')
    // No fact twice on the banner (user clip 2026-08-22): the eyebrow names
    // the window, the headline carries the deadline, the sub adds what
    // happens next, the stats carry the streams and the petty-cash date.
    const eyebrow = inWindow ? `${orderMonthName} order window` : 'month close'
    const pettyStat = {
      k: `${cycleMonthName} petty cash`,
      v: `by ${pettyClose.format('D MMM')}`,
      tip: `${cycleMonthName}'s petty-cash entries — complete them before the book closes ${pettyCloseLabel}`,
      section: 'monthly',
      tab: 'petty-cash'
    }
    const capexOpen = !!(c && c.open)
    const capexDone =
      capexOpen &&
      ['submitted', 'approved', 'sent', 'received'].includes(c.status)
    const pmDone = w.pm_submitted === true
    // Consumables shares the same window (Azam's fill-by asks land on the
    // 22nd–25th every month, ahead of the 29th) — a warning line, no blocking.
    const consDone = w.cons_submitted === true
    const pmStat = {
      k: 'program material',
      v: pmDone ? 'sent ✓' : 'due',
      tip: `Next month's classroom material order — fill it here by ${dueLabel}`,
      section: 'monthly',
      tab: 'program-material'
    }
    const consStat = {
      k: 'consumables',
      v: consDone ? 'sent ✓' : 'due',
      tip: `Next month's consumables requirement — fill it here by ${dueLabel}`,
      section: 'monthly',
      tab: 'consumables'
    }
    const capexStat = {
      k: 'monthly capex',
      v: !capexOpen
        ? 'not open'
        : capexDone
        ? 'sent ✓'
        : c.total > 0
        ? inr(c.total)
        : 'due',
      tip: `The month's capex request against your capex budget — submit by ${dueLabel}`,
      section: 'capex'
    }
    const daysStat = {
      k: 'days left',
      v: daysLeftLabel,
      tip: `Days until the window's ${dueLabel} deadline`
    }
    // Uniform order everywhere (user 2026-08-22): program material →
    // consumables → petty cash (close-out only) → monthly capex.
    // 1. The month's spend has crossed its budget pool (window days only).
    if (inWindow && m && m.base > 0 && m.over)
      return {
        eyebrow,
        headline: `${cycleMonthName} is over budget`,
        sub: 'Review the category spends before placing more orders.',
        stats: [
          {
            k: 'budget',
            v: inr(m.base),
            tip: "This month's budget pool — open the dashboard",
            section: 'monthly',
            tab: 'dashboard'
          },
          {
            k: 'used',
            v: inr(m.used),
            tip: 'Spend recorded so far this month — open the dashboard',
            section: 'monthly',
            tab: 'dashboard'
          }
        ]
      }
    // 2. Everything the window asks for is in — a receipt, not an ask.
    //    In the close-out days the receipt still carries the petty-cash line.
    if (pmDone && consDone && (capexDone || !capexOpen))
      return {
        eyebrow,
        headline: inWindow
          ? 'Your orders are with HQ'
          : `Finish ${cycleMonthName} petty cash`,
        sub: inWindow
          ? 'Nothing due from the centre — materials arrive in the first week.'
          : `Your ${orderMonthName} forms are in.`,
        stats: inWindow
          ? [pmStat, consStat, capexStat]
          : [pmStat, consStat, pettyStat, capexStat]
      }
    // 3. Something is still due — inside the window, or in the close-out days
    //    right after it, before HQ places the orders.
    return {
      eyebrow,
      headline: inWindow
        ? `Forms due by ${dueLabel}`
        : `The window closed ${dueLabel}`,
      sub: inWindow
        ? 'HQ places the orders after the deadline — materials arrive in the first week.'
        : `Send what's pending before HQ places the orders — and finish ${cycleMonthName}'s petty cash.`,
      stats: inWindow
        ? [pmStat, consStat, capexStat, daysStat]
        : [pmStat, consStat, pettyStat, capexStat]
    }
  }

  const renderLanding = () => {
    const activity = signals.activity
    const latestNotifs = signals.notifications?.latest || []
    const strip = stripFor()
    return (
      <div className={classes.landingWrap}>
        {strip && (
          <Paper elevation={0} className={classes.strip}>
            <div>
              <div className={classes.stripMonth}>{strip.eyebrow}</div>
              <div className={classes.stripHeadline}>{strip.headline}</div>
              <div className={classes.stripSub}>{strip.sub}</div>
            </div>
            {/* No primary button (user 2026-08-22) — every stat is the way
                in: click opens its own form, hover explains it. */}
            <div className={classes.stripStats}>
              {strip.stats.map((s) => (
                <div
                  key={s.k}
                  className={s.section ? classes.stripStatLink : undefined}
                  title={s.tip}
                  onClick={
                    s.section
                      ? () => {
                          setSection(s.section)
                          if (s.tab) setTab(s.tab)
                        }
                      : undefined
                  }
                >
                  <div className={classes.stripK}>{s.k}</div>
                  <div className={classes.stripV}>{s.v}</div>
                </div>
              ))}
            </div>
          </Paper>
        )}
        {renderCards()}
        {renderMinorCards()}
        <Paper variant="outlined" className={classes.activity}>
          <Typography className={classes.activityTitle}>
            recent activity — {Dayjs().format('MMMM')}
          </Typography>
          {activity === undefined ? (
            <Typography variant="body2" color="textSecondary">
              Loading…
            </Typography>
          ) : activity.length || latestNotifs.length ? (
            <>
              {activity.map((a) => (
                <div key={a.id} className={classes.activityRow}>
                  <span className={classes.cellChip}>{a.category}</span>
                  <span>{a.description}</span>
                  <span className={`${classes.pill} ${classes.pillGrey}`}>
                    {String(a.status || '').replace(/_/g, ' ')}
                  </span>
                  <span className={classes.activityAmount}>
                    {inr(a.amount)}
                  </span>
                </div>
              ))}
              {latestNotifs.map((n) => (
                <div key={`n-${n.id}`} className={classes.activityRow}>
                  <span className={classes.cellChip}>notification</span>
                  <span>{n.title}</span>
                  {!n.read && (
                    <span className={`${classes.pill} ${classes.pillAmber}`}>
                      unread
                    </span>
                  )}
                  <span className={classes.activityAmount}>
                    {Dayjs(n.created).format('D MMM')}
                  </span>
                </div>
              ))}
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No orders placed this month yet — Program Material, Consumables
              and Petty Cash entries show up here as the team records them.
            </Typography>
          )}
        </Paper>
      </div>
    )
  }

  const renderMinorCards = () => (
    <div className={classes.cellsMinor}>
      {SECTIONS.filter((s) => s.minor).map((s) => {
        const { sigs, pill } = cardContent(s)
        const first = sigs[0]
        return (
          <Paper
            key={s.id}
            variant="outlined"
            className={classes.cellMinor}
            onClick={() => setSection(s.id)}
          >
            <span className={`${classes.iconTile} ${tintClass[s.tint]}`}>
              {ICONS[s.icon]}
            </span>
            <Typography className={classes.cellTitle}>{s.label}</Typography>
            {first && (
              <span className={classes.cellMinorSig}>{first.text}</span>
            )}
            <span className={classes.cellMinorRight}>
              {pill && (
                <span
                  className={`${classes.pill} ${classes[`pill${pill.tone}`]}`}
                >
                  {pill.text}
                </span>
              )}
              <span className={classes.cellArrow}>→</span>
            </span>
          </Paper>
        )
      })}
    </div>
  )

  const renderCards = () => (
    <div className={classes.cells}>
      {SECTIONS.filter((s) => !s.minor).map((s) => {
        const { sigs, pill } = cardContent(s)
        return (
          <Paper
            key={s.id}
            variant="outlined"
            className={classes.cell}
            onClick={() => setSection(s.id)}
          >
            <div className={classes.cellHead}>
              <span className={`${classes.iconTile} ${tintClass[s.tint]}`}>
                {ICONS[s.icon]}
              </span>
              <Typography className={classes.cellTitle}>{s.label}</Typography>
              <span className={classes.cellArrow}>→</span>
            </div>
            <div className={classes.sigList}>
              {sigs.length ? (
                sigs.map((sig, i) => (
                  <span key={i} className={classes.sig}>
                    <span
                      className={`${classes.dot} ${classes[`dot${sig.dot}`]}`}
                    />
                    <span>{sig.text}</span>
                  </span>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {s.blurb}
                </Typography>
              )}
            </div>
            <div className={classes.cellFoot}>
              {s.chips
                ? s.chips.map((c) => (
                    <span key={c} className={classes.cellChip}>
                      {c}
                    </span>
                  ))
                : pill && (
                    <span
                      className={`${classes.pill} ${
                        classes[`pill${pill.tone}`]
                      }`}
                    >
                      {pill.text}
                    </span>
                  )}
            </div>
          </Paper>
        )
      })}
    </div>
  )

  const renderMonthly = () => {
    const active = monthlyTabs.find((t) => t.id === tab) || monthlyTabs[0]
    const Active = active.Comp
    return (
      <>
        <Tabs
          className={classes.tabs}
          value={active.id}
          onChange={(e, v) => {
            setTab(v)
            setNavTarget(null) // manual tab change drops any pending deep-link
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          {monthlyTabs.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              label={t.label}
              classes={{ root: classes.tab }}
            />
          ))}
        </Tabs>
        {centreId ? (
          <Active
            // Key by tab so each tab gets its OWN instance. Petty Cash and R&M
            // share the same component (PettyCash); without this they'd reuse
            // one instance and unsaved draft/state would bleed between ledgers.
            key={active.id}
            centreId={centreId}
            onNavigate={handleNavigate}
            initialMonth={
              navTarget && navTarget.tab === active.id
                ? navTarget.month
                : undefined
            }
            {...(active.extraProps || {})}
          />
        ) : (
          <Box p={4}>
            <Typography variant="body2" color="textSecondary">
              {centres.length
                ? 'Select a centre to continue.'
                : 'Loading centres…'}
            </Typography>
          </Box>
        )}
      </>
    )
  }

  const renderSection = () => {
    if (section === 'monthly') return renderMonthly()
    if (section === 'afterschool')
      return centreId ? (
        <AfterschoolGamesPage
          centreId={centreId}
          centreName={centres.find((c) => c.uuid === centreId)?.name || ''}
        />
      ) : (
        <Box p={4}>
          <Typography variant="body2" color="textSecondary">
            {centres.length
              ? 'Select a centre to continue.'
              : 'Loading centres…'}
          </Typography>
        </Box>
      )
    if (section === 'capex')
      return centreId ? (
        <CapexPage
          centreId={centreId}
          centreName={centres.find((c) => c.uuid === centreId)?.name || ''}
        />
      ) : (
        <Box p={4}>
          <Typography variant="body2" color="textSecondary">
            {centres.length
              ? 'Select a centre to continue.'
              : 'Loading centres…'}
          </Typography>
        </Box>
      )
    if (section === 'notifications')
      return (
        <NotificationsPage centreId={centreId} onNavigate={handleNavigate} />
      )
    if (section === 'settings') return <CataloguePage readOnly />
    return null
  }

  return (
    <ProcurementThemeProvider>
      <Box className={classes.root}>
        <div className={classes.topRow}>
          {section === 'home' && (
            <div className={classes.landingHead}>
              <Typography className={classes.landingTitle}>
                Procurement &amp; Expenses
              </Typography>
              <Typography className={classes.landingSub}>
                what needs {centreName || 'the centre'} today ·{' '}
                {Dayjs().format('dddd, D MMMM')}
              </Typography>
            </div>
          )}
          {section !== 'home' && (
            <Tabs
              className={classes.sectionTabs}
              value={section}
              onChange={(e, v) => setSection(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                value="home"
                icon={<HomeIcon fontSize="small" />}
                classes={{ root: `${classes.sectionTab} ${classes.homeTab}` }}
              />
              {SECTIONS.filter((s) => !s.disabled).map((s) => (
                <Tab
                  key={s.id}
                  value={s.id}
                  label={s.label}
                  classes={{ root: classes.sectionTab }}
                />
              ))}
            </Tabs>
          )}
          <span className={classes.topSpacer} />
          {centrePicker}
        </div>

        {section === 'home' ? renderLanding() : renderSection()}
      </Box>
    </ProcurementThemeProvider>
  )
}

export default ProcurementCenterTeam
